import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { TileLayer } from 'react-leaflet';
import { useSelector } from 'react-redux';
import getCOPScookie from 'utils/cookies';
import useDeepCompareEffect from 'use-deep-compare-effect';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

// Leaflet
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Minimap plugin
import 'leaflet-clonelayer';
import 'leaflet.layerscontrol-minimap';
import 'leaflet.layerscontrol-minimap/control.layers.minimap.css';
import 'leaflet.sync';

import { mapProviderConfigs } from '../../../utils/app-configs';
import MapMarker from './marker';
import MapWrapper from './map-wrapper';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid ${p => p.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F4F7FA;
`;

const GeoMap = ({
  center,
  zoom = 13,
  location,
}) => {
  const [map, setMap] = useState(null);
  const [mapLayers, setMapLayers] = useState([]);
  const [minimapLayers, setMinimapLayers] = useState(null);

  if (location?.length !== 2) {
    return <Wrapper>
      <FormattedMessage id='geoMap.ERROR' />
    </Wrapper>;
  }

  const markerConfig = [
    {
      position: location,
    },
  ];

  const locale = useSelector(state => state.languageProvider.locale);
  // Hardcode to 'openstreet' to test lcoally
  const mapProvider = useSelector(state => state.user.mapProvider);

  const [layerGroupControl, setLayerGroupControl] = useState(null);
  const [zoomControl, setZoomControl] = useState(null);

  // Returns the config for the provider and layer
  const getProviderConfig = (provider) => {
    if (provider === 'openstreet') {
      return mapProviderConfigs[provider];
    }

    if (provider === 'azure') {
      return {
        ...mapProviderConfigs[provider],
        language: locale ?? 'en-US',
        subscriptionKey: getCOPScookie('MapsSubscriptionKey'),
      };
    }

    return null;
  };

  const calculateLayers = (provider, providerConfig) => {
    const mappedLayers = [];

    // Check if the provider has multiple layers configured
    if (mapProviderConfigs[provider]?.layers) {
      for (const [key, value] of Object.entries(mapProviderConfigs[provider].layers)) {
        mappedLayers.push({
          ...providerConfig,
          layerId: value,
          layerName: key,
        });
      }
    } else {
      mappedLayers.push(providerConfig);
    }
    return mappedLayers;
  };

  useEffect(() => {
    if (mapProvider) {
      const providerConfig = getProviderConfig(mapProvider);
      const layers = calculateLayers(mapProvider, providerConfig);
      setMapLayers(layers);
    }
  }, [mapProvider]);

  useDeepCompareEffect(() => {
    // Add zoom control if missing
    if (map) {
      if (!zoomControl) {
        const newZoomControl = L.control.zoom({ position: 'topright' });
        newZoomControl.addTo(map);
        setZoomControl(newZoomControl);
      }

      // Remove all minimap layers from the map
      if (minimapLayers) {
        Object.values(minimapLayers).forEach((layer) => {
          map.removeLayer(layer);
        });

        setMinimapLayers(null);
      }

      layerGroupControl?.remove();
      if (mapLayers?.length > 1) {
        // Reduce the array to an object => format it to how Leaflet needs
        const reducedLayers = mapLayers.reduce((acc, curr, ind) => {
          const newLayer = L.tileLayer(curr.url, { ...curr });
          // Set default layer on map
          if (ind === 0) {
            newLayer.addTo(map);
          }
          acc[curr.layerName] = newLayer;
          return acc;
        }, {});
        setMinimapLayers(reducedLayers);

        if (L.control.layers.minimap) {
          // Create new control & add it to the map
          const layerControl = L.control.layers.minimap(reducedLayers);
          layerControl.addTo(map);

          // Update control ref
          setLayerGroupControl(layerControl);
        }
      }
    }
  }, [mapLayers, map]);

  const getMapCenter = () => {
    // Use configured center
    if (center) {
      return center;
    }

    // Use first marker's position
    if (markerConfig?.length) {
      return markerConfig[0].position;
    }

    // Failsafe, map won't render without a center
    return [0, 0];
  };

  return (
    <>
      <MapWrapper
        ref={setMap}
        center={getMapCenter()}
        zoom={zoom}
        minZoom={1}
        zoomControl={false}
      >
        {/* If only one layer render it here, multiple layers are set in uef */}
        {mapLayers.length === 1 &&
          <TileLayer
            {...mapLayers[0]}
          />
        }
        {markerConfig?.map((marker, i) =>
          <MapMarker
            key={i}
            map={map}
            zoom={zoom}
            {...marker}
          />,
        )}
      </MapWrapper>
    </>
  );
};

GeoMap.propTypes = {
  center: PropTypes.array,
  zoom: PropTypes.number,
  location: PropTypes.array,
};

export default GeoMap;
